import React, { useState, useCallback, useEffect, memo } from 'react';
import { View, Image, StyleSheet, ViewStyle, ActivityIndicator } from 'react-native';
import { Svg, Path } from 'react-native-svg';
import profilePhotoCacheService from '../services/profilePhotoCacheService';

interface AvatarProps {
  uri?: string | null;
  size?: number;
  style?: ViewStyle;
  showInitials?: boolean;
  initials?: string;
  loading?: boolean;
  borderColor?: string;
  borderWidth?: number;
  /** Border width applied on each side while preserving the image size. */
  outerBorderWidth?: number;
  user?: { id: string }; // Assuming user object with id is passed
  refreshProfile?: () => Promise<void>; // Function to refresh user data
  useCache?: boolean; // Whether to use cached profile photos
}

const Avatar = memo(function Avatar({ 
  uri, 
  size = 88, 
  style, 
  loading = false,
  borderColor = '#7E7D75',
  borderWidth = 1,
  outerBorderWidth = 0,
  user,
  refreshProfile,
  useCache = true
}: AvatarProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(!!uri);
  const [imageSource, setImageSource] = useState<{ uri: string } | null>(uri ? { uri } : null);
  const [isMounted, setIsMounted] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);


  // Component mount/unmount tracking
  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Load cached profile photo if enabled and user ID is available
  useEffect(() => {
    if (!isMounted || !useCache || !user?.id) return;
    
    const loadCachedPhoto = async () => {
      try {
        setImageLoading(true);
        const cachedPhotoUrl = await profilePhotoCacheService.getProfilePhoto(user.id);
        
        if (!isMounted) return;
        
        if (cachedPhotoUrl) {
          console.log('📸 Using cached profile photo');
          setImageSource({ uri: cachedPhotoUrl });
          setImageError(false);
        } else {
          console.log('📸 No cached profile photo available');
          setImageSource(null);
        }
      } catch (error) {
        console.error('❌ Error loading cached profile photo:', error);
        if (isMounted) {
          setImageError(true);
        }
      } finally {
        if (isMounted) {
          setImageLoading(false);
        }
      }
    };

    // Only load from cache if no URI is provided directly
    if (!uri) {
      loadCachedPhoto();
    }
  }, [user?.id, useCache, isMounted, uri]);

  // Reset states when URI changes (fallback for direct URI prop)
  useEffect(() => {
    if (!isMounted) return;
    
    if (uri) {
      // Only update if URI has actually changed
      if (imageSource?.uri !== uri) {
        setImageError(false);
        setImageLoading(true);
        setImageSource({ uri });
        console.log('🔄 Avatar URI updated:', uri);
      }
    } else if (!useCache || !user?.id) {
      // Only clear image source if caching is disabled or no user
      if (imageSource !== null) {
        setImageSource(null);
        setImageError(false);
        setImageLoading(false);
      }
    }
  }, [uri, isMounted, useCache, user?.id, imageSource]); // Added imageSource to dependencies for comparison

  const handleImageLoad = useCallback(() => {
    console.log('✅ Avatar image loaded successfully:', imageSource?.uri);
    setImageLoading(false);
    setImageError(false);
    setHasLoadedOnce(true);
  }, [imageSource?.uri]);

  const handleImageError = useCallback((error: any) => {
    console.log('❌ Avatar image failed to load:', imageSource?.uri);
    console.log('Error details:', error.nativeEvent?.error || 'Unknown error');
    setImageError(true);
    setImageLoading(false);
  }, [imageSource?.uri]);

  const handleImageLoadStart = useCallback(() => {
    console.log('🔄 Avatar image load started:', imageSource?.uri);
    setImageLoading(true);
    setImageError(false);
  }, [imageSource?.uri]);

  // Derive border radius from the style prop if provided, otherwise default to circle
  const containerSize = size + (outerBorderWidth * 2);
  const effectiveBorderWidth = outerBorderWidth > 0 ? outerBorderWidth : borderWidth;
  const effectiveBorderRadius = (style as any)?.borderRadius ?? containerSize / 2;

  // Container styles
  const avatarContainerStyle = [
    styles.avatar,
    { 
      width: containerSize, 
      height: containerSize, 
      borderRadius: effectiveBorderRadius,
      borderColor: borderColor,
      borderWidth: effectiveBorderWidth
    },
    style
  ];

  // Image styles
  const imageStyle = [
    styles.avatarImage,
    { 
      width: outerBorderWidth > 0 ? size : size - (borderWidth * 2), 
      height: outerBorderWidth > 0 ? size : size - (borderWidth * 2), 
      borderRadius: outerBorderWidth > 0 ? size / 2 : effectiveBorderRadius,
    }
  ];

  // Placeholder styles
  const placeholderStyle = [
    styles.avatarPlaceholder,
    { 
      width: outerBorderWidth > 0 ? size : size - (borderWidth * 2), 
      height: outerBorderWidth > 0 ? size : size - (borderWidth * 2), 
      borderRadius: outerBorderWidth > 0 ? size / 2 : effectiveBorderRadius,
    }
  ];

  // Show image if URI exists and no error occurred
  if (imageSource && !imageError) {
    return (
      <View style={avatarContainerStyle}>
        <Image
          source={imageSource}
          style={imageStyle}
          onLoad={handleImageLoad}
          onError={handleImageError}
          onLoadStart={handleImageLoadStart}
          resizeMode="cover"
          defaultSource={undefined}
        />
        {(imageLoading || loading) && !hasLoadedOnce && (
          <View style={[placeholderStyle, styles.loadingOverlay]}>
            <ActivityIndicator 
              size={size > 60 ? 'large' : 'small'} 
              color={borderColor} 
            />
          </View>
        )}
      </View>
    );
  }

  // Show loading state for external loading prop
  if (loading) {
    return (
      <View style={avatarContainerStyle}>
        <View style={placeholderStyle}>
          <ActivityIndicator 
            size={size > 60 ? 'large' : 'small'} 
            color={borderColor} 
          />
        </View>
      </View>
    );
  }

  // Fallback to placeholder with user icon (navbar style)
  const iconSize = size * 0.6;
  
  return (
    <View style={avatarContainerStyle}>
      <View style={placeholderStyle}>
        <Svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none">
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.983 15.3462C8.11536 15.3462 4.8125 15.931 4.8125 18.2729C4.8125 20.6148 8.0944 21.2205 11.983 21.2205C15.8506 21.2205 19.1525 20.6348 19.1525 18.2938C19.1525 15.9529 15.8715 15.3462 11.983 15.3462Z"
            fill="#151515"
            stroke="#151515"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.983 12.0059C14.5211 12.0059 16.5782 9.94779 16.5782 7.40969C16.5782 4.8716 14.5211 2.81445 11.983 2.81445C9.44487 2.81445 7.38675 4.8716 7.38675 7.40969C7.3782 9.93922 9.42201 11.9973 11.9506 12.0059H11.983Z"
            fill="white"
            stroke="#151515"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      </View>
    </View>
  );
});

export default Avatar;

const styles = StyleSheet.create({
  avatar: {
    backgroundColor: '#DAD8CF',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    resizeMode: 'cover',
  },
  avatarPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#DAD8CF',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(218, 216, 207, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});